// Touch-equivalent of ContextMenu. Mobile uses this bottom-sheet
// instead of the right-click menu. Exposes the same actions in the
// same order so muscle memory transfers between devices. Each action
// closes the sheet before firing its handler.
//
// `single` indicates whether exactly one target is selected; actions
// that don't apply to multi-selection (Rename, Preview, Copy chunk
// id) are hidden in that case rather than shown disabled.
//
// Optional `header` renders above the action list — used by
// FileInfoSheet to show a rich type-icon + filename + metadata block
// for non-media files. When omitted, the sheet is purely the action
// list (the original selection-action-sheet presentation).

import * as React from "react";
import {
  ArrowRight,
  Copy,
  Download,
  Eye,
  Pencil,
  Share2,
  Trash2,
} from "lucide-react";
import { BottomSheet } from "@/components/ui/bottom-sheet";
import { cn } from "@/lib/utils";

interface ItemActionSheetProps {
  open: boolean;
  onOpenChange: (next: boolean) => void;
  title: string;
  single: boolean;
  header?: React.ReactNode;
  onExport: () => void;
  onShare?: () => void;
  onPreview?: () => void;
  onRename: () => void;
  onMove: () => void;
  onDelete: () => void;
  onCopyChunkId?: () => void;
}

export function ItemActionSheet({
  open,
  onOpenChange,
  title,
  single,
  header,
  onExport,
  onShare,
  onPreview,
  onRename,
  onMove,
  onDelete,
  onCopyChunkId,
}: ItemActionSheetProps) {
  const run = (fn: () => void) => () => {
    onOpenChange(false);
    fn();
  };

  return (
    <BottomSheet
      open={open}
      onOpenChange={onOpenChange}
      title={title}
      className="p-0 md:p-0"
    >
      {header && <div className="px-4 pt-4 pb-3 md:px-5 md:pt-5 md:pb-4">{header}</div>}
      <ul className="flex flex-col py-1">
        <Action icon={<Download className="h-5 w-5" />} onClick={run(onExport)}>
          Export to disk
        </Action>
        {onShare && single && (
          <Action icon={<Share2 className="h-5 w-5" />} onClick={run(onShare)}>
            Share…
          </Action>
        )}
        {single && onPreview && (
          <Action icon={<Eye className="h-5 w-5" />} onClick={run(onPreview)}>
            Preview
          </Action>
        )}
        <Separator />
        {single && (
          <Action icon={<Pencil className="h-5 w-5" />} onClick={run(onRename)}>
            Rename
          </Action>
        )}
        <Action icon={<ArrowRight className="h-5 w-5" />} onClick={run(onMove)}>
          Move to…
        </Action>
        {single && onCopyChunkId && (
          <>
            <Separator />
            <Action
              icon={<Copy className="h-5 w-5" />}
              onClick={run(onCopyChunkId)}
            >
              Copy chunk id
            </Action>
          </>
        )}
        <Separator />
        <Action
          icon={<Trash2 className="h-5 w-5" />}
          onClick={run(onDelete)}
          danger
        >
          Delete
        </Action>
      </ul>
    </BottomSheet>
  );
}

function Action({
  icon,
  children,
  onClick,
  danger,
}: {
  icon: React.ReactNode;
  children: React.ReactNode;
  onClick: () => void;
  danger?: boolean;
}) {
  return (
    <li>
      <button
        type="button"
        onClick={onClick}
        className={cn(
          "flex w-full items-center gap-3 px-4 py-3 text-left text-text-soft hover:bg-accent-bg hover:text-text active:bg-accent-bg",
          danger && "text-danger hover:bg-danger/10 hover:text-danger",
        )}
      >
        <span className={cn("text-text-dim", danger && "text-danger")}>
          {icon}
        </span>
        <span className="flex-1 text-base">{children}</span>
      </button>
    </li>
  );
}

function Separator() {
  return <li className="my-1 h-px bg-border" aria-hidden="true" />;
}
